import { useMutation } from '@apollo/client';
import type { InsertIngestionConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertIngestionConfigDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type InsertIngestionConfigInput,
  useInsertIngestionConfigTRPC,
} from './useInsertIngestionConfigTRPC';

/**
 * Flag-gated wrapper for the `insertIngestionConfig` mutation. Defaults to the
 * legacy Apollo mutation and delegates to the tRPC variant when the `trpc`
 * feature flag is enabled. Consumers import THIS, never the `*TRPC` twin.
 */
export const useInsertIngestionConfig = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertIngestionConfigDocument,
    {
      update: (cache) => {
        evictField(cache, 'ingestion_config');
      },
    }
  );

  const trpcMutation = useInsertIngestionConfigTRPC();

  const insertIngestionConfig = async (
    input: InsertIngestionConfigInput
  ): Promise<InsertIngestionConfigMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertIngestionConfig(input);
    }

    const result = await insertGraphQL({
      variables: {
        object: {
          IngestionConfig: input.IngestionConfig ?? undefined,
          ApiKey: input.ApiKey ?? undefined,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to insert ingestion config');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertIngestionConfig,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertIngestionConfig,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
